import { Component, Inject } from '@angular/core';
import { MAT_SNACK_BAR_DATA, MatSnackBarRef } from '@angular/material/snack-bar';
import { SnackbarIconIDs } from '../../../core/feedback.service';
import { CustomPopupComponent, PopupPanelClasses } from '../custom-popup/custom-popup.component';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

export class CustomSnackbarData {
  title?: string;
  userFriendlyMessage: string; // will be shown directly in the snackbar
  detailedInfoFromBE?: string[]; // will be passed to popup (opened with "Show details"-button in the snackbar); e.g. BE error messages
  actionButtons?: { text: string, action: Function }[]; //action buttons and which function to run after user clicks on them
  dismissButton?: { text?: string, action?: Function, hide?: boolean }; // on default, a dismiss button is shown with text "Dismiss" & no action
  selectedAction?: Function; //don't pass; used as a return value after selection by the user
}

export enum SnackbarPanelClasses {
  SUCCESS = 'success-snackbar',
  INFO = 'info-snackbar',
  WARNING = 'warning-snackbar',
  ALERT = 'alert-snackbar'
}

/**
 * Component for an info / success / warning / error snackbar. Just assign the correct panelClass
 * (see below) when opening the snackbar to assign the correct styling (colors and icon).
 * Contains:
 * 1. an icon (automatically set, depending on panelClass)
 * 2. a message (for multiple messages/lines just add "\n" to the string),
 * 3. multiple action buttons (optional) for which a custom text can be assigned; the snackbar is dismissedWithAction when clicked
 * 4. a dismiss-button (shown by default but optional, can be edited & hidden via "dismissButton" config-parameter)
 */
@Component({
    selector: 'app-custom-snackbar',
    templateUrl: './custom-snackbar.component.html',
    styleUrls: ['./custom-snackbar.component.scss'],
    standalone: true,
    imports: [
        MatIcon,
        MatButton,
        TranslateModule,
    ],
})
export class CustomSnackbarComponent {
  constructor(
      private snackBarRef: MatSnackBarRef<CustomSnackbarComponent>,
      @Inject(MAT_SNACK_BAR_DATA) public data: CustomSnackbarData,
      private dialog: MatDialog,
  ) {
    snackBarRef.containerInstance.snackBarConfig.horizontalPosition = 'left';
    snackBarRef.containerInstance.snackBarConfig.verticalPosition = 'bottom';
  }

  closeSnackbarWithAction(action: Function) {
    this.data.selectedAction = action;
    this.snackBarRef.dismissWithAction();
  }

  closeSnackbar() {
    this.snackBarRef.dismiss();
  }

  showDetailsPopup() {
    let panelClass: PopupPanelClasses;

    if (this.isOfPanelClass(SnackbarPanelClasses.ALERT)) {
      panelClass = PopupPanelClasses.ALERT;
    }
    if (this.isOfPanelClass(SnackbarPanelClasses.WARNING)) {
      panelClass = PopupPanelClasses.WARNING;
    }
    if (this.isOfPanelClass(SnackbarPanelClasses.SUCCESS)) {
      panelClass = PopupPanelClasses.SUCCESS;
    }
    if (this.isOfPanelClass(SnackbarPanelClasses.INFO)) {
      panelClass = PopupPanelClasses.INFO;
    }

    this.openPopup(
        this.data.title,
        this.data.detailedInfoFromBE,
        panelClass);
  }

  /**
   * Opens a popup that shows additional details.
   * @param actionPerformed The action during which the error occurred (will be shown in the header of the popup).
   * @param errorMessages Error message(s) of the error(s) that occurred.
   * @param popupPanelClass
   */
  openPopup(
      actionPerformed: string,
      errorMessages: string[],
      popupPanelClass: PopupPanelClasses,
  ) {
    this.dialog
        .open(CustomPopupComponent, {
          // stops focusing the first focusable element (applied focus overlay looks horrible on
          // copy-to-clipboard button)
          autoFocus: false,
          panelClass: popupPanelClass,
          data: {
            actionPerformed: actionPerformed,
            messages: errorMessages,
          },
        });
  }

  getLeftIcon(): string {
    if (this.isOfPanelClass(SnackbarPanelClasses.INFO)) {
      return SnackbarIconIDs.INFO;
    }
    if (this.isOfPanelClass(SnackbarPanelClasses.SUCCESS)) {
      return SnackbarIconIDs.SUCCESS;
    }
    if (this.isOfPanelClass(SnackbarPanelClasses.ALERT)) {
      return SnackbarIconIDs.ALERT;
    }
    if (this.isOfPanelClass(SnackbarPanelClasses.WARNING)) {
      return SnackbarIconIDs.WARNING;
    }
    return null;
  }

  isOfPanelClass(panelClass: string): boolean {
    const panelClasses: string | string[] = this.snackBarRef.containerInstance.snackBarConfig.panelClass;
    if (typeof panelClasses === 'string') {
      return panelClasses === panelClass;
    } else {
      return panelClasses.indexOf(panelClass) !== -1;
    }
  }

}
